import { serviceHelper } from '../config/service-helper';
import Cookies from 'js-cookie';
import { COOKIES } from '../config/cookies';
import { spendAndSavePrefix } from '@jbhi-fi/utils/src/toBasketPromos';
import { toBasketPromos } from '@jbhi-fi/utils/src/toBasketPromos';
import { IsAttachedService } from '../templates/components/pdp/utils/product-service';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { ShopifyStorefrontClient } from '../api/shopify/ShopifyStorefrontClient';
import { Cart, CartResponseLineItemType, CartResponseType } from '@jbhi-fi/apis/src/cart/types';
import { ExistingLineItem } from '@jbhi-fi/apis/src/storefront/StorefrontClient';
import { getCoupons } from './coupons';
import { JbCart, JbCartItem } from '@jbhi-fi/apis/src/shopify/types/JbCart';
import { Coupon } from '@jbhi-fi/apis/src/customer';
import { CartCoupon } from '../contexts/CouponContextProvider/types';
import { getLocal, LocalStorageKeys } from './localStorage';
import { StoreLocation } from '../templates/components/localisation/utils/types';

const storefrontClient = new ShopifyStorefrontClient();

const doAttributesNeedUpdate = (
  existingAttributes: Record<string, string | undefined>,
  newAttributes: Record<string, string | undefined>
) => {
  for (const key in newAttributes) {
    if (existingAttributes[key] !== newAttributes[key]) {
      return true;
    }
  }
  return false;
};

const cartCoupons = (cart: JbCart) => {
  const coupons = cart.attributes?._coupons ? JSON.parse(cart.attributes._coupons) : [];
  return coupons.map((item: { code?: string }) => {
    return item?.code;
  });
};

const getCouponCodes = (coupons: Array<Coupon>): Array<string> =>
  Array.isArray(coupons) ? coupons.map(coupon => coupon.couponCode) : [];

export const getPrimaryItem = (items: Array<JbCartItem>, serviceLink: string | undefined) => {
  return items.find(
    item => item.properties?._serviceLink && item.properties?._serviceLink === serviceLink && !IsAttachedService(item)
  );
};

const shopifyCartToCartApiData = (cart: JbCart) => {
  const cartData: Cart = {
    CartType: window.Jbhifi.customerDetails?.isPerks ? 'Perks' : 'Retail',
    LineItems: [],
    LocationId: 115,
    CouponReferences: cartCoupons(cart),
    CouponReferencesWallet: getCouponCodes(window.Jbhifi.coupons),
  };

  cart.items.forEach(item => {
    const serviceLinks = [];
    let quantity = item.quantity;
    if (IsAttachedService(item)) {
      const primaryItem = getPrimaryItem(cart.items, item.properties?._serviceLink);

      if (primaryItem) quantity = primaryItem.quantity;
    }
    if (item.properties._serviceLink) serviceLinks.push(item.properties._serviceLink);

    cartData.LineItems.push({
      Id: item.line_id,
      SKU: item.sku,
      Quantity: quantity,
      UnitPriceInc: item.final_line_price / 100,
      ServiceLinks: serviceLinks,
      TieredId: item.properties?._marketplaceTieredId,
      IsAttachedService: IsAttachedService(item),
    });
  });

  return cartData;
};

export const splitMismatchedItems = async (cartResponse: CartResponseType, cart: JbCart) => {
  const unsplitCartItems = cartResponse.LineItems.filter(item => item.Id?.match(/-\d$/));
  if (!unsplitCartItems.length) return;
  let updatedCart;
  for (const unsplitItem of unsplitCartItems) {
    if (!unsplitItem.Id) continue;

    const originalItemId = unsplitItem.Id.replace(/-\d$/, '');
    const originalItem = cart.items.find(item => item.line_id === originalItemId);
    if (!originalItem) continue;

    await storefrontClient.updateCartLine(originalItem.line_id, 1);
    updatedCart = await storefrontClient.addToCart(originalItem.id, 1, filterAndMapAttributes(originalItem.properties));
  }
  return updatedCart;
};

export const updateCartWithCartApiResponse = async (cartResponse: CartResponseType, cart: JbCart) => {
  const userAgent = window?.navigator?.userAgent;

  const lineItemPromotions = toBasketPromos(cartResponse.LineItems);

  const couponsInWallet = cartResponse?.WalletCouponsEvaluated || [];
  const primaryItemsToUpdate = cart.items?.map(item => {
    const hasServiceOffering = cartResponse.LineItems.some(
      cartResponseItem => cartResponseItem.Id === item.line_id && cartResponseItem.Services.length
    );
    const itemPromo = lineItemPromotions.find(promo => promo?.promoId === item.line_id);

    // Merge items that have no promos or services
    if (!itemPromo && !hasServiceOffering && !item.properties._serviceLink) {
      delete item.properties._shouldSplit;
    }

    return {
      lineId: item.line_id,
      quantity: item.quantity,
      attributes: filterAndMapAttributes({ ...item.properties, _promotion_id: itemPromo?.promoId }),
    };
  });

  const servicesToUpdate: Array<ExistingLineItem> = [];
  cart.items
    .filter(item => item.properties._isAttachedService === 'true')
    .forEach(item => {
      const basketItem = cartResponse.LineItems.find(basketItem => basketItem.Id === item.line_id);
      if (!basketItem) return;

      const hasPriceMismatch = basketItem.LineTotalInc * 100 !== item.final_line_price;
      const hasValidationErrors = basketItem.ValidationErrors?.length > 0;
      if (hasPriceMismatch || hasValidationErrors) {
        servicesToUpdate.push({
          lineId: item.line_id,
          attributes: filterAndMapAttributes({
            ...item.properties,
            _servicePrice: basketItem.LineTotalInc.toString(),
            _hasServiceErrors: hasValidationErrors,
          }),
        });
      }
    });

  let updatedCart;
  const cartLinesToUpdate = [...primaryItemsToUpdate, ...servicesToUpdate];
  if (cartLinesToUpdate?.length) {
    updatedCart = await storefrontClient.updateCartLines(cartLinesToUpdate);
  }

  let lineItemsCouponPromotions: CartResponseLineItemType['Promotions'] = [];
  cartResponse.LineItems.forEach(lineItem => {
    const itemCouponDiscount = lineItem.Promotions.filter(
      promo => promo?.Reference && promo.PromotionType === 'Coupon'
    );
    lineItemsCouponPromotions = [...lineItemsCouponPromotions, ...itemCouponDiscount];
  });

  const transformedCartDiscounts = JSON.stringify(
    lineItemsCouponPromotions
      .filter(promotion => promotion.CouponName?.startsWith(spendAndSavePrefix))
      .map(promotion => {
        return {
          code: promotion.Reference,
          message: promotion.Name,
          amount: promotion.DiscountInc,
        };
      })
  );

  const cartLevelDiscountCoupon = lineItemsCouponPromotions.find(promotion =>
    promotion.CouponName?.startsWith(spendAndSavePrefix)
  )?.Reference;
  let cartLevelExcludedSkus;
  if (cartLevelDiscountCoupon) {
    const items = cartResponse.LineItems;

    const skusWithCoupon = new Set(
      items
        .filter(item => item.Promotions.some(promo => promo.Reference === cartLevelDiscountCoupon))
        .map(item => item.SKU)
    );

    cartLevelExcludedSkus = JSON.stringify([
      ...new Set(items.filter(item => !skusWithCoupon.has(item.SKU)).map(item => item.SKU)),
    ]);
  }

  const riskifiedToken = Cookies.get(COOKIES.RISKIFIED_TOKEN);
  const { coupons: updatedCoupons } = await getCoupons(undefined, cart, cartResponse);

  const existingCoupons: Array<CartCoupon> = JSON.parse(cart.attributes._coupons || '[]');
  const existingCouponsMap = new Map(existingCoupons.map(coupon => [coupon.code, coupon]));
  const uniqueCoupons = updatedCoupons.map((coupon: { code: string }) => ({
    ...existingCouponsMap.get(coupon.code),
    ...coupon,
  }));

  const newAttributes: Record<string, string | undefined> = {
    _cartDiscounts: transformedCartDiscounts,
    _userAgent: userAgent,
    _riskifiedToken: riskifiedToken,
    _basketSerial: JSON.stringify(lineItemPromotions),
    _excludedSkus: cartLevelExcludedSkus,
    _coupons: JSON.stringify(uniqueCoupons),
    _couponsInWallet: JSON.stringify(couponsInWallet),
  };

  if (window.Jbhifi.localisationSettings.shouldSaveStoreToCart) {
    const preferredStore = getLocal(LocalStorageKeys.MySelectedStore, true) as StoreLocation | undefined;
    if (preferredStore) {
      newAttributes['_preferredStore'] = `${preferredStore.id}|${preferredStore.name}`;
    }
  }

  if (doAttributesNeedUpdate(cart.attributes, newAttributes)) {
    updatedCart = await storefrontClient.setCartAttributes(cart.attributes, newAttributes);
  }

  return updatedCart || cart;
};

export const getCartApiResponse = async (cart: JbCart): Promise<CartResponseType | undefined> => {
  if (!cart) {
    console.error('No cart provided to getBasketResponse');
    return;
  }
  const basketPostData = shopifyCartToCartApiData(cart);
  const calculatePricesUrl = `${serviceHelper.themeApisUrl}/cartApi`;

  try {
    const response = await fetch(calculatePricesUrl, {
      method: 'POST',
      body: JSON.stringify(basketPostData),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('Error fetching basket response', response);
      return;
    }
    const basketResponse = (await response.json())?.data;

    // No proper response
    if (!basketResponse?.LineItems?.length) {
      return;
    }

    return basketResponse;
  } catch (error) {
    console.error('CartApi may be down');
    return;
  }
};
